import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Columns3, RotateCcw, X } from 'lucide-react';
import {
  TICKET_TABLE_COLUMNS,
  type TicketColumnId,
  type TicketTableColumn,
  type TicketTableSettings,
} from './ticket-table-settings';

export function TicketColumnSettingsPanel({ settings, onVisibilityChange, onReset, onClose }: {
  settings: TicketTableSettings;
  onVisibilityChange: (columnId: TicketColumnId, visible: boolean) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  return <aside className="ticket-column-settings-panel" role="dialog" aria-label="Настройка столбцов">
    <header>
      <span><Columns3 /></span>
      <div><b>Столбцы таблицы</b><small>Выберите информацию для списка заявок</small></div>
      <button type="button" onClick={onClose} aria-label="Закрыть настройку столбцов"><X /></button>
    </header>
    <div className="ticket-column-settings-list">
      {TICKET_TABLE_COLUMNS.map(column => {
        const checked = settings.visible.includes(column.id);
        return <label className={checked ? 'selected' : ''} key={column.id}>
          <input
            type="checkbox"
            checked={checked}
            disabled={column.required}
            onChange={event => onVisibilityChange(column.id, event.target.checked)}
          />
          <i aria-hidden="true"><span /></i>
          <span><b>{column.label}</b><small>{column.description}{column.required ? ' · всегда отображается' : ''}</small></span>
          <em>{settings.widths[column.id]} px</em>
        </label>;
      })}
    </div>
    <footer>
      <p>Ширина сохраняется автоматически. Тяните границу столбца в заголовке таблицы.</p>
      <button type="button" onClick={onReset}><RotateCcw /> Стандартный вид</button>
    </footer>
  </aside>;
}

export function TicketColumnResizeHandle({ column, width, onResize }: {
  column: TicketTableColumn;
  width: number;
  onResize: (width: number) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  function startResize(event: ReactPointerEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current?.();

    const startX = event.clientX;
    let lastX = startX;
    let animationFrame = 0;
    setDragging(true);
    document.body.classList.add('ticket-column-resizing');

    const publish = () => {
      animationFrame = 0;
      onResize(width + lastX - startX);
    };
    const move = (pointerEvent: PointerEvent) => {
      lastX = pointerEvent.clientX;
      if (!animationFrame) animationFrame = window.requestAnimationFrame(publish);
    };
    const finish = (pointerEvent?: PointerEvent) => {
      if (pointerEvent) lastX = pointerEvent.clientX;
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      onResize(width + lastX - startX);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      window.removeEventListener('blur', cancel);
      window.removeEventListener('keydown', onWindowKeyDown);
      document.body.classList.remove('ticket-column-resizing');
      setDragging(false);
      cleanupRef.current = null;
    };
    const cancel = () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      window.removeEventListener('blur', cancel);
      window.removeEventListener('keydown', onWindowKeyDown);
      document.body.classList.remove('ticket-column-resizing');
      setDragging(false);
      cleanupRef.current = null;
    };
    const onWindowKeyDown = (keyboardEvent: globalThis.KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') cancel();
    };

    cleanupRef.current = cancel;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    window.addEventListener('blur', cancel);
    window.addEventListener('keydown', onWindowKeyDown);
  }

  function resizeWithKeyboard(event: KeyboardEvent<HTMLSpanElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    onResize(width + (event.key === 'ArrowRight' ? 16 : -16));
  }

  return <span
    className={`ticket-column-resize-handle ${dragging ? 'dragging' : ''}`}
    role="separator"
    aria-label={`Изменить ширину столбца «${column.label}»`}
    aria-orientation="vertical"
    aria-valuemin={column.minWidth}
    aria-valuemax={column.maxWidth}
    aria-valuenow={width}
    tabIndex={0}
    onPointerDown={startResize}
    onKeyDown={resizeWithKeyboard}
  />;
}
